import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { SiteProvider, useSite } from "@/context/SiteContext";
import { DepartmentsProvider } from "@/context/DepartmentsContext";
import { Layout } from "@/components/Layout";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Analytics } from "@/components/Analytics";

import HomePage from "@/pages/HomePage";
import ContactPage from "@/pages/ContactPage";
import { DepartmentsListPage, DepartmentDetailPage } from "@/pages/Departments";
import { BlogListPage, BlogDetailPage } from "@/pages/Blog";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";

import FezabilitateDigitalaPage from "@/pages/services/FezabilitateDigitala";
import DigitalizarePage from "@/pages/services/Digitalizare";
import MarketingPage from "@/pages/services/Marketing";
import DezvoltareWebsitePage from "@/pages/services/DezvoltareWebsite";
import StudiuIdentificareSolutieITPage from "@/pages/services/StudiuIdentificareSolutieIT";
import ProiectInformaticDigitalizarePage from "@/pages/services/ProiectInformaticDigitalizare";
import AuditDESIPage from "@/pages/services/AuditDESI";
import AuditMaturitateDigitalaPage from "@/pages/services/AuditMaturitateDigitala";

import ServicePage from "@/pages/services/ServicePage";
import { useUrl } from "@/context/SiteContext";
import ImprimarePlasticPage from "@/pages/services/ImprimarePlastic";
import ImprimareFibraCarbonPage from "@/pages/services/ImprimareFibraCarbon";
import ImprimareMetalPage from "@/pages/services/ImprimareMetal";

function SolutiiImprimare3DPage() {
  const url = useUrl();
  return (
    <ServicePage
      backLabel="Tehnologii imprimare 3D"
      backTo={url.dept("tehnologii-imprimare-3d")}
      number="00"
      eyebrow="Fabricație aditivă"
      title={["Soluții", "de imprimare 3D."]}
      heroImage="https://images.unsplash.com/photo-1597765654525-5cb60d312ef6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwM2QlMjBwcmludGluZ3xlbnwwfHx8YmxhY2tfYW5kX3doaXRlfDE3ODMxNDU1ODl8MA&ixlib=rb-4.1.0&q=85"
      tagline="Transformăm planuri, schițe sau modele CAD în obiecte fizice funcționale — servicii de scanare 3D, imprimare 3D și inginerie inversă pentru industria producătoare."
      intro={[
        "Prototipare 3D, scanare 3D și printare 3D — trei servicii integrate, un singur partener. Realizăm modele 3D ale obiectelor reale pentru prototipare, inginerie inversă sau design; producem piese de înaltă calitate în serie limitată cu imprimante 3D de nivel industrial.",
      ]}
      bullets={[
        "Piese în serie mică — de la 1 la 100 de bucăți.",
        "Prototipuri — producție rapidă și precisă dintr-o varietate de materiale.",
        "Matrițe de ștanțare — pentru o serie largă de aplicații.",
        "Inginerie inversă — piese de schimb, redesenare, reparare obiecte deteriorate.",
      ]}
      programs={[
        "Automotive", "Feroviar", "Construcții", "Inginerie",
        "Aerospațial", "Larg consum", "Militar", "Medicină",
        "Educație & Cercetare", "Arhitectură & Artă",
      ]}
      stepsEyebrow="Avantajele noastre"
      stepsTitle="Trei valori care contează."
      steps={[
        {
          title: "Rapiditate & flexibilitate",
          desc: "Producem piese și componente în termen scurt, chiar și pentru comenzi mici. Timp redus de producție, cost per bucată optimizat.",
        },
        {
          title: "Personalizare",
          desc: "Realizăm piese și componente adaptate exact nevoilor specifice ale clienților — geometrii complexe imposibil de realizat prin metode tradiționale.",
        },
        {
          title: "Calitate de top",
          desc: "Tehnologie de ultimă generație pentru precizie dimensională maximă, densitate până la 98%, rezistență comparabilă cu aluminiul prelucrat.",
        },
      ]}
      linkGridEyebrow="Alege tehnologia"
      linkGridTitle="Trei tehnologii, trei clase de piese."
      linkGrid={[
        {
          n: "01",
          title: "Piese din plastic",
          desc: "Selective Laser Sintering (SLS) — precizie înaltă, materiale versatile, prototipuri și componente finale.",
          tags: "PA12 · Flexa · PLA · PETG · TPU · PVA · PET-CF · PA6-CF · PC · ABS · ASA",
          to: "/imprimare-piese-3d-din-plastic",
        },
        {
          n: "02",
          title: "Piese din fibră de carbon",
          desc: "Continuous Fiber Reinforcement (CFR) pe Markforged Mark Two — rezistența aluminiului, geometrii complexe.",
          tags: "Onyx™ · Nylon · Fibră carbon · sticlă · Kevlar® · HSHT",
          to: "/imprimare-piese-3d-din-fibra-de-carbon",
        },
        {
          n: "03",
          title: "Piese din metal",
          desc: "Bound Metal Deposition (BMD) + sinterizare — densități până la 98%, oțeluri, aliaje și titan.",
          tags: "17-4 PH · 316L · 4140 · A2 · D2 · H13 · Cupru · IN625 · Ti64",
          to: "/imprimare-piese-3d-din-metal",
        },
      ]}
      servicesEyebrow="Inginerie inversă"
      servicesTitle="Modele 3D precise ale obiectelor reale."
      services={[
        {
          title: "Piese de schimb",
          desc: "Realizăm modele 3D precise ale obiectelor reale pentru fabricarea de piese de schimb.",
        },
        {
          title: "Reparare obiecte deteriorate",
          desc: "Realizăm modele 3D precise ale obiectelor reale pentru repararea obiectelor deteriorate.",
        },
        {
          title: "Redesenare obiecte",
          desc: "Realizăm modele 3D precise ale obiectelor reale pentru redesenarea lor — îmbunătățirea performanței și funcționalității.",
        },
      ]}
    />
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const { urls } = useSite();
  // Disable smooth scroll on admin routes (they have their own scroll needs)
  const smooth = !pathname.startsWith("/admin");
  return (
    <SmoothScroll enabled={smooth}>
      <Toaster position="top-right" toastOptions={{
        style: { border: "1px solid #0A0A0A", borderRadius: 0, background: "#fff", color: "#0A0A0A", fontFamily: "IBM Plex Mono, monospace" }
      }} />
      <Analytics />
      <Routes>
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path={urls.departments} element={<Layout><DepartmentsListPage /></Layout>} />
        <Route path="/departamente/solutii-de-imprimare-3d" element={<Layout><SolutiiImprimare3DPage /></Layout>} />
        <Route path={`${urls.departments}/:id`} element={<Layout><DepartmentDetailPage /></Layout>} />
        <Route path={urls.blog} element={<Layout><BlogListPage /></Layout>} />
        <Route path={`${urls.blog}/:slug`} element={<Layout><BlogDetailPage /></Layout>} />
        <Route path={urls.contact} element={<Layout><ContactPage /></Layout>} />
        <Route path="/fezabilitate-digitala" element={<Layout><FezabilitateDigitalaPage /></Layout>} />
        <Route path="/digitalizare" element={<Layout><DigitalizarePage /></Layout>} />
        <Route path="/marketing" element={<Layout><MarketingPage /></Layout>} />
        <Route path="/dezvoltare-website" element={<Layout><DezvoltareWebsitePage /></Layout>} />
        <Route path="/studiu-privind-identificarea-solutiei-it-digitalizare" element={<Layout><StudiuIdentificareSolutieITPage /></Layout>} />
        <Route path="/proiect-informatic-pentru-digitalizare" element={<Layout><ProiectInformaticDigitalizarePage /></Layout>} />
        <Route path="/audit-desi-pentru-digitalizare" element={<Layout><AuditDESIPage /></Layout>} />
        <Route path="/audit-de-maturitate-digitala" element={<Layout><AuditMaturitateDigitalaPage /></Layout>} />
        <Route path="/imprimare-piese-3d-din-plastic" element={<Layout><ImprimarePlasticPage /></Layout>} />
        <Route path="/imprimare-piese-3d-din-fibra-de-carbon" element={<Layout><ImprimareFibraCarbonPage /></Layout>} />
        <Route path="/imprimare-piese-3d-din-metal" element={<Layout><ImprimareMetalPage /></Layout>} />
        <Route path="/admin" element={<Layout><AdminLoginPage /></Layout>} />
        <Route path="/admin/dashboard" element={<Layout><AdminDashboardPage /></Layout>} />
        <Route path="*" element={
          <Layout>
            <div className="mx-auto max-w-[1440px] px-6 py-32 text-center" data-testid="not-found-page">
              <div className="font-mono-label text-black/60">404</div>
              <div className="mt-4 font-display font-medium uppercase text-6xl">Pagină negăsită</div>
            </div>
          </Layout>
        } />
      </Routes>
    </SmoothScroll>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SiteProvider>
          <DepartmentsProvider>
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </DepartmentsProvider>
        </SiteProvider>
      </AuthProvider>
    </div>
  );
}
